import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { List } from "lucide-react";

import { useDelayedFlag, useTran } from "@/hooks";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { useEsgDashboardStore } from "../../hooks";

const PerformanceCharts = observer(() => {
  const { t } = useTran();
  const {
    categoryProgress,
    dashboardYear,
    loading,
    ensureData,
    openMetricsModal,
  } = useEsgDashboardStore();

  const showSkeleton = useDelayedFlag(loading);

  useEffect(() => {
    ensureData();
  }, [ensureData]);

  if (showSkeleton) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="border-b border-border">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-2 h-3 w-64" />
        </CardHeader>
        <CardContent className="p-6">
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!categoryProgress.length) {
    return (
      <div className="rounded-radius border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        {t("No performance data available yet.")}
      </div>
    );
  }

  const chartData = categoryProgress.map((item) => ({
    category: item.category,
    "Actual Progress (%)": item.percentage,
    "Target (%)": 100,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-radius border border-border bg-card p-3 text-xs shadow-sm">
        <p className="mb-1 font-semibold text-foreground">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} className="text-muted-foreground">
            <span className="font-medium text-foreground">{entry.name}:</span>{" "}
            {entry.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border bg-muted/30 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-foreground">
              {t("Annual Progress by Category")} ({dashboardYear})
            </p>
            <p className="text-xs text-muted-foreground">
              {t("Completion percentage vs 100% target for {{year}}", {
                year: dashboardYear,
              })}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={openMetricsModal}>
            <List className="mr-2 h-4 w-4" />
            {t("View Metrics")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} maxBarSize={90}>
            <CartesianGrid strokeDasharray="3 3" stroke="#cbd5f5" />
            <XAxis
              dataKey="category"
              tick={{ fill: "#64748b", fontSize: 12 }}
            />
            <YAxis tick={{ fill: "#64748b", fontSize: 12 }} domain={[0, 100]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine
              y={100}
              stroke="#94a3b8"
              strokeDasharray="5 5"
              label={{ value: t("Target"), position: "right", fill: "#94a3b8" }}
            />
            <Bar
              dataKey="Actual Progress (%)"
              fill="#10b981"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="Target (%)"
              fill="#cbd5e1"
              opacity={0.3}
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
});

export default PerformanceCharts;
